// rc118 — Mover de domicilio: la ONU cambia de PON/OLT con la MISMA serie.
// Desaprovisiona la config actual en ByteOLT y deja un PRE-registro de la
// misma serie apuntando a la OLT destino — al reconectarla en el domicilio
// nuevo, el sondeo la autoriza sola en el PON donde aparezca (o al instante
// si ya está visible como huérfana ahí). PPPoE, router y ByteACS NO se tocan;
// el mover de router/segmento/IP sigue siendo la sección Conexión del editor.
import { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { ApiError } from "@/lib/api";
import {
  useOnusHuerfanas, useOltsByteolt, usePlantillasOlt, usePerfilesOlt,
} from "@/features/integraciones/api";
import { useMoverOnu } from "./api";
import type { ContratoDetail } from "./schemas";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contrato: ContratoDetail;
  /** Se llama al terminar (cerrar y refrescar). */
  onDone?: () => void;
};

export function MoverOnuDialog({ open, onOpenChange, contrato, onDone }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [oltId, setOltId] = useState("");
  const [templateSel, setTemplateSel] = useState("");
  const [perfilSel, setPerfilSel] = useState("");

  const serial = contrato.fiber_detalle?.onu_serial ?? "";

  // enabled del alta reusado solo para saber si la integración está viva
  const { data: byteoltData } = useOnusHuerfanas(open);
  const byteoltEnabled = byteoltData?.enabled;
  const { data: oltsData } = useOltsByteolt(open && byteoltEnabled === true);
  const oltSel = (oltsData ?? []).find((o) => o.id === oltId);
  const { data: plantillas } = usePlantillasOlt(oltId || null);
  const { data: perfiles } = usePerfilesOlt(oltId || null);

  useEffect(() => {
    setTemplateSel("");
    setPerfilSel("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oltId]);

  useEffect(() => {
    if (open) {
      setError(null);
      setMotivo("");
      setOltId("");
      setTemplateSel("");
      setPerfilSel("");
    }
  }, [open]);

  const mut = useMoverOnu(contrato.id);

  async function submit() {
    setError(null);
    if (motivo.trim().length < 5) {
      setError("Escribe el motivo del movimiento (mínimo 5 caracteres).");
      return;
    }
    if (!oltId) {
      setError("Elige la OLT destino.");
      return;
    }
    if (oltSel?.vendor === "vsol" && !templateSel && (plantillas?.length ?? 0) > 0) {
      setError("Elige la plantilla de servicio (VLANs) para esa OLT.");
      return;
    }
    try {
      const res = await mut.mutateAsync({
        motivo: motivo.trim(),
        olt_id: oltId,
        ...(templateSel ? { template_id: templateSel } : {}),
        ...(perfilSel ? { line_profile: perfilSel } : {}),
      });
      toast.success(
        res.esperando
          ? `ONU en espera de conexión en ${res.olt ?? "la OLT destino"} — se autoriza sola al conectarla.`
          : `ONU aprovisionada de una vez en ${res.olt ?? "la OLT destino"}.`,
      );
      if (res.aviso) toast.warning(res.aviso);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      setError((e as ApiError).message ?? "No se pudo mover la ONU");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md">
      <DialogHeader>
        <DialogTitle>Mover ONU de domicilio — {contrato.codigo}</DialogTitle>
        <DialogDescription>
          Misma serie, PON/OLT nuevos. PPPoE, router y ByteACS no se tocan.
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        {byteoltEnabled === false ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            <strong>Integración ByteOLT desactivada.</strong> Actívala en
            Ajustes › Integraciones para mover la ONU desde aquí.
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              <AlertTriangle size={13} className="mt-0.5 flex-none" />
              <span>
                <strong>Desconecta la ONU del domicilio anterior ANTES de mover.</strong>{" "}
                Se borra la config del PON viejo y la serie{" "}
                <strong className="font-mono">{serial || "—"}</strong> queda
                pre-registrada en la OLT destino: se autoriza sola en el PON donde
                aparezca. Si sigue conectada donde estaba, el sondeo la
                re-aprovisiona ahí mismo.
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>OLT destino (puede ser la misma, si solo cambia el PON)</Label>
                <Select value={oltId} onChange={(e) => setOltId(e.target.value)}>
                  <option value="">Selecciona la OLT…</option>
                  {(oltsData ?? [])
                    .filter((o) => !o.read_only)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.nombre}{o.modelo ? ` · ${o.modelo}` : ""}
                      </option>
                    ))}
                </Select>
              </div>
              {oltId && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Plantilla de servicio (VLANs)</Label>
                    <Select
                      value={templateSel}
                      onChange={(e) => setTemplateSel(e.target.value)}
                    >
                      <option value="">
                        {oltSel?.vendor === "tplink"
                          ? "TP-Link: las VLANs viven en el perfil — elige solo el perfil"
                          : (plantillas?.length ?? 0) === 0
                            ? "Sin plantillas en esta OLT (VLAN default)"
                            : "Selecciona plantilla…"}
                      </option>
                      {(plantillas ?? []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — datos {t.vlan_datos}
                          {t.vlan_aprov ? ` + aprov ${t.vlan_aprov}` : ""}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label>Perfil ONU</Label>
                    <Select
                      value={perfilSel}
                      onChange={(e) => setPerfilSel(e.target.value)}
                    >
                      <option value="">auto — default de la OLT</option>
                      {(perfiles ?? []).map((p, i) => {
                        const name = p.name ?? p.nombre ?? "";
                        return name ? (
                          <option key={`${name}-${i}`} value={name}>{name}</option>
                        ) : null;
                      })}
                    </Select>
                  </div>
                </div>
              )}
              <div>
                <Label>Motivo del movimiento *</Label>
                <Textarea
                  rows={2}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ej. Cliente se mudó a otra dirección / cambio de NAP"
                />
              </div>
            </div>

            <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
              ¿También cambia el router, segmento o IP del lado ByteISP? Eso va en la
              sección <strong>Conexión</strong> del editor, como siempre. Si al mover
              te marca error porque la OLT &quot;aún ve&quot; la ONU en el lugar
              viejo, corre un sondeo en ByteOLT y reintenta.
            </p>

            {error && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button
          type="button" size="sm"
          onClick={submit}
          disabled={mut.isPending || byteoltEnabled === false}
        >
          {mut.isPending ? "Moviendo…" : "Mover ONU"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
